import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { dataViz, palette, radius, space, spacing, typography } from '../../../style-utils/theme';
import * as api from '../../../api/admin-client';
import { DashTooltip } from '../dashboard/dashboard-screen/charts/chart-tooltip';
import { useAdminResource } from './use-admin';
import { AdminTable, Tag, Tone, when, whenExact, num } from './admin-table';
import { clockTimeSeconds, deviceZone, formatIn } from '../../../function-utils/building-time';
import {
  Card,
  CardHead,
  CardTitle,
  CardBody,
  Tile,
  TileGrid,
  TileLabel,
  TileValue,
  TileFoot,
  Toolbar,
  Select,
  SearchBox,
  Btn,
  Empty,
  ErrorBox,
  NoteBox,
  DefList,
  Split,
  Bar,
  Mono,
  SkeletonRow,
} from './admin.styled';

/**
 * THE BOX, ITS DATABASE, AND EVERY LOG LINE IT WRITES.
 *
 * Two screens, both fed by src/ops/* on the backend:
 *
 *   Server       /admin/ops/health — memory, swap, pressure, CPU, disk, systemd
 *                units, TLS expiry, the backup, sysstat's 10-minute history, the
 *                app's own heap breakdown, the database probe, and ONE verdict
 *                that names every threshold it tripped.
 *   System logs  /admin/ops/logs — the journal: app, proxy, backup, SSH, kernel,
 *                OS updates, or everything. Filters live in the URL, so a view
 *                of "every 5xx for this organisation in the last hour" is a link
 *                that can be pasted into a ticket.
 *
 * ── BEFORE THIS, ALL OF IT WAS SSH ─────────────────────────────────────────
 *
 * On 2026-09-10 the only way to learn the app held 790 MB on a 912 MB box was to
 * read /proc by hand, and no request had ever been logged by the app or by
 * Caddy. These screens are that terminal session, made permanent.
 *
 * ── CHARTS ─────────────────────────────────────────────────────────────────
 *
 * One unit per chart (MB, or %), never two y-axes. Colours are the validated
 * `dataViz.series` slots in order (design_system.md §2.4); status colours are
 * kept for thresholds and always ship with words. Every chart has a hover
 * tooltip through the one house tooltip, and fewer than two points renders a
 * sentence rather than a lone dot (reference-one-bucket-charts).
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const n = (v: unknown) => num(v);

const Loading: React.FC = () => (
  <div aria-busy="true">
    {Array.from({ length: 5 }).map((_, i) => (
      /* eslint-disable-next-line react/no-array-index-key */
      <SkeletonRow key={i} />
    ))}
  </div>
);

const Panel: React.FC<{
  title: string;
  children: React.ReactNode;
  foot?: React.ReactNode;
  aside?: React.ReactNode;
}> = ({ title, children, foot, aside }) => (
  <Card>
    <CardHead>
      <CardTitle>{title}</CardTitle>
      {aside}
    </CardHead>
    <CardBody>
      {children}
      {foot ? <div style={{ marginTop: spacing.sm }}>{foot}</div> : null}
    </CardBody>
  </Card>
);

const Facts: React.FC<{ rows: [string, React.ReactNode][] }> = ({ rows }) => (
  <DefList>
    {rows.map(([k, v]) => (
      <React.Fragment key={k}>
        <dt>{k}</dt>
        <dd>{v}</dd>
      </React.Fragment>
    ))}
  </DefList>
);

const duration = (s: number | null | undefined): string => {
  if (s == null) return '—';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);

  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`;
};

// The reader's own clock, like the rest of the console (see admin-table).
const clock = (t: number) =>
  formatIn(t, deviceZone(), {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

/* ═══════════════════════════════════════════════════════════════════════════
   ONE CHART
   ═══════════════════════════════════════════════════════════════════════════ */

interface SeriesDef {
  key: string;
  label: string;
}

const LegendRow = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.sm};
  margin: 0 0 ${spacing.xs};
  padding: 0;
  list-style: none;
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};

  li {
    display: inline-flex;
    align-items: center;
    gap: ${spacing.half};
  }
`;

const Swatch = styled.span<{ tone: string }>`
  width: 12px;
  height: 3px;
  border-radius: 2px;
  background: ${p => p.tone};
`;

const TimeChart: React.FC<{
  data: Row[];
  series: SeriesDef[];
  unit: string;
  yMax?: number;
  describe: string;
}> = ({ data, series, unit, yMax, describe }) => {
  const rows = useMemo(
    () => data.filter(d => d.ts).map(d => ({ ...d, t: Date.parse(d.ts) })),
    [data]
  );
  if (rows.length < 2) {
    return (
      <Empty>
        {rows.length ? 'One point so far — the line starts at the next sample.' : 'No samples yet.'}
      </Empty>
    );
  }
  const tones = series.map((_, i) => dataViz.series[i]);

  return (
    <figure style={{ margin: 0 }} aria-label={describe}>
      {series.length > 1 ? (
        <LegendRow aria-hidden="true">
          {series.map((s, i) => (
            <li key={s.key}>
              <Swatch tone={tones[i]} />
              {s.label}
            </li>
          ))}
        </LegendRow>
      ) : null}
      <div style={{ width: '100%', height: 200 }}>
        <ResponsiveContainer>
          <LineChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={dataViz.grid} vertical={false} />
            <XAxis
              dataKey="t"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              tickFormatter={v => clock(Number(v))}
              tick={{ fontSize: 11, fill: dataViz.axis }}
              stroke={dataViz.grid}
              minTickGap={48}
            />
            <YAxis
              width={48}
              domain={[0, yMax ?? 'auto']}
              tick={{ fontSize: 11, fill: dataViz.axis }}
              stroke={dataViz.grid}
              tickFormatter={v => `${v}${unit === '%' ? '%' : ''}`}
            />
            <Tooltip
              content={
                <DashTooltip
                  series={series.map((s, i) => ({
                    key: s.key,
                    label: `${s.label} (${unit})`,
                    tone: tones[i],
                  }))}
                  labelOf={raw => whenExact(Number(raw))}
                />
              }
            />
            {series.map((s, i) => (
              <Line
                key={s.key}
                dataKey={s.key}
                name={s.label}
                stroke={tones[i]}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </figure>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   SERVER
   ═══════════════════════════════════════════════════════════════════════════ */

const unitTone = (u: Row): Tone => {
  if (!u.found) return 'warning';
  if (u.kind === 'oneshot') return u.result === 'success' ? 'success' : 'alert';
  if (u.active === 'active') return 'success';

  return u.kind === 'timer' ? 'warning' : 'alert';
};

const Verdict = styled.div<{ tone: 'ok' | 'warning' | 'alert' }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  padding: ${space(2)};
  border-radius: ${radius.sm};
  border: 1px solid ${palette.line};
  border-left: 4px solid
    ${p => (p.tone === 'alert' ? palette.alert : p.tone === 'warning' ? palette.warning : palette.success)};
  background: ${palette.surface};

  strong {
    font-size: ${typography.size.sm}px;
  }
  ul {
    margin: 0;
    padding-left: ${spacing.sm};
  }
  li {
    font-size: ${typography.size.xs}px;
    margin: ${spacing.half} 0;
  }
`;

const HISTORY_DAYS = [1, 2, 7, 14];

export const ServerScreen: React.FC = () => {
  const [days, setDays] = useState(1);
  const { data, loading, error, reload } = useAdminResource<Row>(`/ops/health?days=${days}`);

  // Every 30s. A health screen that has to be refreshed by hand is a snapshot.
  useEffect(() => {
    const t = setInterval(reload, 30_000);

    return () => clearInterval(t);
  }, [days]);

  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading && !data) return <Loading />;
  if (!data) return <Loading />;

  const v = data.verdict || { status: 'ok', alerts: [] };
  const m = data.memory || {};
  const cpu = data.cpu || {};
  const pr = data.pressure || {};
  const disk = data.disk || {};
  const proc = data.process || {};
  const pm = proc.memory || {};
  const db = data.database || {};
  const dbl = db.latest || null;
  const availPct = m.total_mb ? Math.round((m.available_mb / m.total_mb) * 100) : null;
  const units: Row[] = (data.systemd && data.systemd.units) || [];
  const certs: Row[] = (data.certificates && data.certificates.hosts) || [];
  const history: Row[] = (data.history && data.history.points) || [];

  return (
    <>
      <Verdict tone={v.status} role={v.status === 'ok' ? 'status' : 'alert'}>
        <strong>
          {v.status === 'ok'
            ? 'Healthy — nothing has crossed a threshold.'
            : `${v.alerts.length} ${v.alerts.length === 1 ? 'thing needs' : 'things need'} attention`}
        </strong>
        {v.alerts.length ? (
          <ul>
            {v.alerts.map((a: Row) => (
              <li key={`${a.code}-${a.message}`}>
                <Tag tone={a.level === 'alert' ? 'alert' : 'warning'}>{a.level}</Tag> {a.message}
              </li>
            ))}
          </ul>
        ) : null}
        <Mono>
          {data.host?.hostname} · {data.host?.kernel} · checked {when(data.at)} · refreshes every 30s
        </Mono>
      </Verdict>

      <TileGrid>
        <Tile tone={availPct != null && availPct < 10 ? 'alert' : availPct != null && availPct < 20 ? 'warning' : undefined}>
          <TileLabel>RAM available</TileLabel>
          <TileValue>{n(m.available_mb)} MB</TileValue>
          <TileFoot>
            of {n(m.total_mb)} MB · {m.used_pct}% used
            <Bar pct={m.used_pct || 0} tone={availPct != null && availPct < 10 ? 'alert' : undefined} />
          </TileFoot>
        </Tile>
        <Tile tone={m.swap_used_pct > 50 ? 'alert' : m.swap_used_pct > 25 ? 'warning' : undefined}>
          <TileLabel>Swap</TileLabel>
          <TileValue>{n(m.swap_used_mb)} MB</TileValue>
          <TileFoot>
            {m.swap_used_pct ?? '—'}% of {n(m.swap_total_mb)} MB
            {data.paging?.swap_in_per_s != null
              ? ` · ${data.paging.swap_in_per_s}/${data.paging.swap_out_per_s} pages/s in/out`
              : ''}
          </TileFoot>
        </Tile>
        <Tile tone={pr.memory?.some?.avg60 > 20 ? 'alert' : pr.memory?.some?.avg60 > 5 ? 'warning' : undefined}>
          <TileLabel>Memory pressure</TileLabel>
          <TileValue>{pr.memory?.some ? `${pr.memory.some.avg60}%` : '—'}</TileValue>
          <TileFoot>time stalled on memory, last minute</TileFoot>
        </Tile>
        <Tile>
          <TileLabel>CPU</TileLabel>
          <TileValue>{cpu.busy_pct != null ? `${cpu.busy_pct}%` : '—'}</TileValue>
          <TileFoot>
            load {(cpu.load || []).join(' / ') || '—'} on {n(cpu.cores)} cores
          </TileFoot>
        </Tile>
        <Tile tone={disk.used_pct > 90 ? 'alert' : disk.used_pct > 80 ? 'warning' : undefined}>
          <TileLabel>Disk {disk.mount}</TileLabel>
          <TileValue>{disk.used_pct != null ? `${disk.used_pct}%` : '—'}</TileValue>
          <TileFoot>
            {disk.free_gb} GB free of {disk.total_gb} GB
            <Bar pct={disk.used_pct || 0} tone={disk.used_pct > 90 ? 'alert' : disk.used_pct > 80 ? 'warning' : undefined} />
          </TileFoot>
        </Tile>
        <Tile>
          <TileLabel>App process</TileLabel>
          <TileValue>{n(pm.rss)} MB</TileValue>
          <TileFoot>
            native {n(pm.native_other)} · heap {n(pm.heap_used)} · swap {n(pm.swap)} MB
          </TileFoot>
        </Tile>
        <Tile tone={dbl && !dbl.ok ? 'alert' : dbl && dbl.latency_ms > 200 ? 'warning' : dbl ? 'success' : undefined}>
          <TileLabel>Database</TileLabel>
          <TileValue>{dbl ? (dbl.ok ? `${dbl.latency_ms} ms` : 'down') : '—'}</TileValue>
          <TileFoot>
            {dbl
              ? dbl.ok
                ? `${n(dbl.conns_server)}/${n(dbl.max_conns)} server connections · pool ${n(dbl.pool_size)}/${n(dbl.pool_max)}`
                : dbl.error
              : 'first probe 5s after the app starts'}
          </TileFoot>
        </Tile>
        <Tile>
          <TileLabel>Uptime</TileLabel>
          <TileValue>{duration(data.host?.uptime_s)}</TileValue>
          <TileFoot>app {duration(proc.uptime_s)} · Node {proc.node}</TileFoot>
        </Tile>
      </TileGrid>

      <Panel
        title="Box memory and swap"
        aside={
          <Toolbar>
            <Select
              aria-label="History window"
              value={days}
              onChange={e => setDays(Number(e.target.value))}
            >
              {HISTORY_DAYS.map(d => (
                <option key={d} value={d}>
                  last {d} {d === 1 ? 'day' : 'days'}
                </option>
              ))}
            </Select>
          </Toolbar>
        }
        foot={
          data.history && !data.history.available ? (
            <NoteBox>{data.history.reason}</NoteBox>
          ) : (
            <Mono>sysstat, every 10 minutes, kept 28 days on the box</Mono>
          )
        }
      >
        <TimeChart
          data={history}
          unit="%"
          yMax={100}
          series={[
            { key: 'mem_used_pct', label: 'RAM used' },
            { key: 'swap_used_pct', label: 'Swap used' },
          ]}
          describe="Percentage of RAM and of swap in use on the box over the selected window"
        />
      </Panel>

      <Split>
        <Panel
          title="App process memory"
          foot={
            <NoteBox>
              <strong>native</strong> is memory that is neither the JavaScript heap nor a Buffer
              Node knows about — the part that grew to ~600 MB on 2026-09-10. Each request
              in System logs carries how much memory moved across it (<Mono>drss</Mono>).
            </NoteBox>
          }
        >
          <TimeChart
            data={(proc.memory_history || []).map((p: Row) => ({ ...p, total: (p.rss || 0) + (p.swap || 0) }))}
            unit="MB"
            series={[
              { key: 'total', label: 'Resident + swapped' },
              { key: 'native_other', label: 'Native' },
              { key: 'heap_used', label: 'JS heap' },
            ]}
            describe="The app process's memory per minute since it started, split into native memory and JavaScript heap"
          />
        </Panel>
        <Panel title="Database round trip" foot={<NoteBox>{db.blind_spot}</NoteBox>}>
          <TimeChart
            data={(db.history || []).filter((p: Row) => p.ok)}
            unit="ms"
            series={[{ key: 'latency_ms', label: 'SELECT 1' }]}
            describe="Milliseconds for a database round trip, sampled every minute"
          />
          {dbl && dbl.ok ? (
            <Facts
              rows={[
                ['Pool', `${n(dbl.pool_size)} open · ${n(dbl.pool_available)} free · ${n(dbl.pool_waiting)} waiting (max ${n(dbl.pool_max)})`],
                ['Server connections', `${n(dbl.conns_server)} of ${n(dbl.max_conns)} (ours ${n(dbl.conns)})`],
                ['Idle in transaction', n(dbl.idle_in_txn)],
                ['Longest open transaction', `${n(dbl.longest_txn_s)}s`],
                ['Lock waits', n(dbl.lock_waits)],
                ['Last minute', `${n(dbl.deadlocks)} deadlocks · ${dbl.rollback_pct ?? '—'}% rolled back · cache hit ${dbl.cache_hit_pct ?? '—'}%`],
                ['Size · xid age', `${n(dbl.size_mb)} MB · ${n(dbl.xid_age)}`],
              ]}
            />
          ) : null}
        </Panel>
      </Split>

      <Panel title="Services and timers">
        {data.systemd && !data.systemd.available ? (
          <NoteBox>{data.systemd.reason}</NoteBox>
        ) : (
          <AdminTable<Row>
            rows={units}
            rowKey={r => r.id}
            columns={[
              { key: 'id', header: 'Unit', render: r => <Mono>{r.id}</Mono> },
              {
                key: 'state',
                header: 'State',
                render: r => (
                  <Tag tone={unitTone(r)}>
                    {!r.found ? 'not installed' : r.kind === 'oneshot' ? `last run: ${r.result}` : `${r.active}/${r.sub}`}
                  </Tag>
                ),
              },
              {
                key: 'when',
                header: 'Since / last run',
                render: r => (r.kind === 'oneshot' ? when(r.last_exit) : r.kind === 'timer' ? when(r.last_trigger) : when(r.since)),
              },
              { key: 'next', header: 'Next', wide: true, render: r => (r.next_trigger ? when(r.next_trigger) : '—') },
              {
                key: 'restarts',
                header: 'Crash restarts',
                numeric: true,
                render: r =>
                  r.restarts ? <strong style={{ color: palette.alert }}>{n(r.restarts)}</strong> : n(r.restarts),
              },
              { key: 'mem', header: 'Memory', numeric: true, wide: true, render: r => (r.memory_mb != null ? `${n(r.memory_mb)} MB` : '—') },
            ]}
          />
        )}
      </Panel>

      <Split>
        <Panel title="TLS certificates">
          {certs.length ? (
            <AdminTable<Row>
              rows={certs}
              rowKey={r => r.host}
              columns={[
                { key: 'h', header: 'Host', render: r => <Mono>{r.host}</Mono> },
                {
                  key: 'd',
                  header: 'Expires',
                  render: r =>
                    r.days_left != null ? (
                      <Tag tone={r.days_left < 10 ? 'alert' : r.days_left < 20 ? 'warning' : 'success'}>
                        {r.days_left} days
                      </Tag>
                    ) : (
                      <Tag tone="warning">{r.error || 'unknown'}</Tag>
                    ),
                },
                { key: 'i', header: 'Issuer', wide: true, render: r => r.issuer || '—' },
              ]}
            />
          ) : (
            <Empty>{data.certificates?.note || 'No https hosts configured.'}</Empty>
          )}
        </Panel>
        <Panel title="Logs on the box">
          <Facts
            rows={[
              [
                'Journal',
                data.journal?.readable ? (
                  <Tag tone="success">readable · {data.journal.disk_usage}</Tag>
                ) : (
                  <Tag tone="warning">not readable</Tag>
                ),
              ],
              ['Why', data.journal?.readable ? 'System logs shows the full journal.' : data.journal?.reason || '—'],
              ['OOM kills since boot', n(data.paging?.oom_kills_since_boot)],
              ['I/O pressure', pr.io?.some ? `${pr.io.some.avg60}% stalled, last minute` : '—'],
            ]}
          />
        </Panel>
      </Split>

      <Toolbar>
        <Btn onClick={reload}>Refresh now</Btn>
      </Toolbar>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   BACKUPS
   ═══════════════════════════════════════════════════════════════════════════ */

const fmtBytes = (v: unknown): string => {
  const b = Number(v || 0);
  if (!b) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(b) / Math.log(1024)));

  return `${(b / 1024 ** i).toFixed(i ? 1 : 0)} ${units[i]}`;
};

const ago = (iso?: string | null): string => {
  if (!iso) return '—';
  const h = (Date.now() - Date.parse(iso)) / 3600e3;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min ago`;
  if (h < 48) return `${Math.round(h)}h ago`;

  return `${Math.round(h / 24)} days ago`;
};

const REASON_LABEL: Record<string, { label: string; tone: Tone }> = {
  nightly: { label: 'nightly', tone: 'neutral' },
  premigrate: { label: 'before a migration', tone: 'brand' },
  manual: { label: 'by hand', tone: 'warning' },
  kept: { label: 'kept by hand', tone: 'neutral' },
};

/**
 * Every database backup that exists, on the box and in S3, merged by filename.
 * The API reads the backup job's manifest (the dumps themselves are root-only
 * and never leave the box through this screen) and lists S3 with a key that
 * can read the listing but not the files.
 */
export const BackupsScreen: React.FC = () => {
  const { data, loading, error, reload } = useAdminResource<Row>('/ops/backups');

  useEffect(() => {
    const t = setInterval(reload, 60_000);

    return () => clearInterval(t);
  }, []);

  if (error) return <ErrorBox role="alert">{error}</ErrorBox>;
  if (loading && !data) return <Loading />;
  if (!data) return <Loading />;

  const alerts: Row[] = data.alerts || [];
  const rows: Row[] = data.backups || [];
  const newest = data.newest;
  const last = data.box?.last_run;
  const status = alerts.some(a => a.level === 'alert') ? 'alert' : alerts.length ? 'warning' : 'ok';

  return (
    <>
      <Verdict tone={status} role={status === 'ok' ? 'status' : 'alert'}>
        <strong>
          {status === 'ok'
            ? `Backups are current — the newest was taken ${ago(newest?.taken_at)} and is copied off the box.`
            : `${alerts.length} backup ${alerts.length === 1 ? 'problem' : 'problems'}`}
        </strong>
        {alerts.length ? (
          <ul>
            {alerts.map(a => (
              <li key={a.code}>
                <Tag tone={a.level === 'alert' ? 'alert' : 'warning'}>{a.level}</Tag> {a.message}
              </li>
            ))}
          </ul>
        ) : null}
      </Verdict>

      <TileGrid>
        <Tile tone={!newest ? 'alert' : undefined}>
          <TileLabel>Newest backup</TileLabel>
          <TileValue>{ago(newest?.taken_at)}</TileValue>
          <TileFoot>{newest ? whenExact(newest.taken_at) : 'none found'}</TileFoot>
        </Tile>
        <Tile>
          <TileLabel>Available</TileLabel>
          <TileValue>{n(data.totals?.count)}</TileValue>
          <TileFoot>
            {n(data.totals?.on_box)} on the box · {n(data.totals?.in_s3)} in S3
          </TileFoot>
        </Tile>
        {/* DISABLED IS NOT THE SAME AS BROKEN, and it is the more serious of
            the two. A destination that cannot be listed is a permissions
            problem; a destination that does not exist means every backup
            shares a failure domain with the database it protects. Alert, not
            warning, and said in words rather than left as a count of zero. */}
        <Tile
          tone={
            data.s3?.enabled === false ? 'alert' : data.s3?.available === false ? 'warning' : undefined
          }
        >
          <TileLabel>Off-box copies</TileLabel>
          <TileValue>{data.s3?.enabled === false ? 'Disabled' : n(data.totals?.in_s3)}</TileValue>
          <TileFoot>
            {data.s3?.enabled === false
              ? 'Backups exist only on this box'
              : data.s3?.available
                ? `${fmtBytes(data.totals?.bytes_s3)} · ${data.s3.uri}`
                : data.s3?.reason}
          </TileFoot>
        </Tile>
        <Tile tone={last?.upload_error ? 'alert' : undefined}>
          <TileLabel>Last run</TileLabel>
          <TileValue>{last ? ago(last.at) : '—'}</TileValue>
          <TileFoot>
            {last
              ? `${REASON_LABEL[last.reason]?.label || last.reason} · ${n(last.tables)} tables · ${last.upload_error ? 'S3 copy FAILED' : last.uploaded_to ? 'copied off-box' : data.s3?.enabled === false ? 'on this box only' : 'not copied'}`
              : data.box?.reason || 'unknown'}
          </TileFoot>
        </Tile>
      </TileGrid>

      <NoteBox>{data.retention}</NoteBox>

      <Panel title="Every backup, newest first">
        {rows.length ? (
          <AdminTable<Row>
            rows={rows}
            rowKey={r => r.name}
            columns={[
              {
                key: 'when',
                header: 'Taken',
                render: r => (
                  <span title={r.taken_at || ''}>
                    {r.taken_at ? whenExact(r.taken_at) : '—'}{' '}
                    <span style={{ color: palette.slate }}>({ago(r.taken_at)})</span>
                  </span>
                ),
              },
              {
                key: 'why',
                header: 'Type',
                render: r => {
                  const x = REASON_LABEL[r.reason] || { label: r.reason, tone: 'neutral' as Tone };

                  return <Tag tone={x.tone}>{x.label}</Tag>;
                },
              },
              { key: 'size', header: 'Size', numeric: true, render: r => fmtBytes(r.bytes) },
              {
                key: 'box',
                header: 'On the box',
                render: r => (r.on_box ? <Tag tone="success">yes</Tag> : <span style={{ color: palette.slate }}>rotated out</span>),
              },
              {
                key: 's3',
                header: 'In S3',
                render: r =>
                  r.in_s3 ? <Tag tone="success">{r.s3_folder || 'yes'}</Tag> : <Tag tone="warning">no</Tag>,
              },
              { key: 'tables', header: 'Tables', numeric: true, wide: true, render: r => (r.tables != null ? n(r.tables) : '—') },
              { key: 'file', header: 'File', wide: true, render: r => <Mono>{r.name}</Mono> },
            ]}
          />
        ) : (
          <Empty>No backups visible yet.</Empty>
        )}
      </Panel>

      <Split>
        <Panel title="Where they are">
          <Facts
            rows={[
              ['On the box', data.box?.available ? `/var/backups/lobbero — manifest updated ${ago(data.box.generated_at)}` : data.box?.reason || '—'],
              ['Off the box', data.s3?.available ? data.s3.uri : data.s3?.reason || '—'],
              ['Who can delete', 'Nobody from the app: the backup key can only add. The box job and the S3 lifecycle rule remove anything over 7 days.'],
            ]}
          />
        </Panel>
        <Panel title="Restoring">
          <NoteBox>{data.restore}</NoteBox>
        </Panel>
      </Split>

      <Toolbar>
        <Btn onClick={reload}>Refresh now</Btn>
      </Toolbar>
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   SYSTEM LOGS
   ═══════════════════════════════════════════════════════════════════════════ */

const RANGES: { value: string; label: string; ms: number | null }[] = [
  { value: '15m', label: 'Last 15 minutes', ms: 15 * 60e3 },
  { value: '1h', label: 'Last hour', ms: 3600e3 },
  { value: '6h', label: 'Last 6 hours', ms: 6 * 3600e3 },
  { value: '24h', label: 'Last 24 hours', ms: 24 * 3600e3 },
  { value: '7d', label: 'Last 7 days', ms: 7 * 86400e3 },
  { value: '30d', label: 'Last 30 days', ms: 30 * 86400e3 },
  { value: 'all', label: 'All time', ms: null },
];

const LEVELS = [
  { value: '7', label: 'Every level' },
  { value: '6', label: 'Info and worse' },
  { value: '5', label: 'Notice and worse' },
  { value: '4', label: 'Warnings and worse' },
  { value: '3', label: 'Errors only' },
];

const TAGS = ['Req', 'access', 'DB', 'Mem', 'Boot', 'API', 'FATAL', 'Cron', 'Sweep', 'Auth', 'Admin', 'Stripe', 'SendGrid', 'Push'];

const DEFAULT_SOURCES = ['app'];
const TEXT_FILTERS = ['q', 'request_id', 'route', 'org', 'uid', 'min_ms'] as const;

const levelTone = (l: number): Tone => (l <= 3 ? 'alert' : l === 4 ? 'warning' : l === 5 ? 'brand' : 'neutral');

const LogList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  font-family: ${typography.mono};
  font-size: ${typography.size.xxs}px;
`;

const LogRow = styled.li<{ level: number; open: boolean }>`
  border-bottom: 1px solid ${palette.line};
  border-left: 3px solid
    ${p => (p.level <= 3 ? palette.alert : p.level === 4 ? palette.warning : 'transparent')};
  background: ${p => (p.open ? palette.tealTintSoft : 'transparent')};
`;

const LogHead = styled.button`
  display: grid;
  /* 13ch fits HH:MM:SS.mmm on one line (11ch wrapped the milliseconds); 9ch
     fits the widest level tag, "warning". */
  grid-template-columns: 13ch 9ch 6ch minmax(0, 1fr);
  gap: ${spacing.xs};
  align-items: baseline;
  width: 100%;
  padding: ${space(0.5)} ${spacing.xs};
  border: 0;
  background: none;
  font: inherit;
  color: ${palette.ink};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${palette.surfaceMuted};
  }
  &:focus-visible {
    outline: 2px solid ${palette.teal};
    outline-offset: -2px;
  }
  span {
    min-width: 0;
  }
  .msg {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .time {
    color: ${palette.slate};
  }
  @media (max-width: 720px) {
    grid-template-columns: 13ch minmax(0, 1fr);
    .lvl,
    .src {
      display: none;
    }
  }
`;

const LogBody = styled.div`
  padding: 0 ${spacing.xs} ${spacing.xs} ${spacing.sm};
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};

  pre {
    margin: 0;
    padding: ${spacing.xs};
    background: ${palette.surface};
    border: 1px solid ${palette.line};
    border-radius: ${radius.xs};
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    max-height: 320px;
    overflow: auto;
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.half};

  label {
    display: inline-flex;
    align-items: center;
    gap: ${spacing.half};
    padding: ${spacing.half} ${spacing.xs};
    border: 1px solid ${palette.lineStrong};
    border-radius: ${radius.pill};
    font-size: ${typography.size.xxs}px;
    cursor: pointer;
    background: ${palette.surface};
  }
  input:checked + span {
    font-weight: ${typography.weight.semibold};
  }
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: ${spacing.xs};

  input,
  select {
    width: 100%;
    max-width: none;
    min-width: 0;
  }
`;

/** A request line, summarised: status, method, path, duration. */
const RequestSummary: React.FC<{ e: api.LogEntry }> = ({ e }) => {
  const f = e.fields || {};
  const s = Number(f.s) || 0;
  const tone: Tone = s >= 500 ? 'alert' : s >= 400 ? 'warning' : s ? 'success' : 'neutral';

  return (
    <>
      <Tag tone={tone}>{s || 'aborted'}</Tag> {f.m} {f.p}{' '}
      <span style={{ color: palette.slate }}>
        {f.ms != null ? `${f.ms}ms` : ''}
        {f.drss ? ` · ${f.drss > 0 ? '+' : ''}${f.drss} MB` : ''}
      </span>
    </>
  );
};

const fmtTs = (e: api.LogEntry) => {
  const d = new Date(e.ts);

  return (
    clockTimeSeconds(e.ts, deviceZone()) + '.' + String(d.getMilliseconds()).padStart(3, '0')
  );
};

const LogLine: React.FC<{
  e: api.LogEntry;
  sources: string;
  onFilter: (key: string, value: string) => void;
  canContext: boolean;
}> = ({ e, sources, onFilter, canContext }) => {
  const [open, setOpen] = useState(false);
  const [ctx, setCtx] = useState<api.LogContext | null>(null);
  const [ctxErr, setCtxErr] = useState<string | null>(null);
  const isReq = (e.tag === 'Req' || e.tag === 'access') && e.fields;

  const loadContext = async () => {
    setCtxErr(null);
    try {
      setCtx(await api.getLogContext(e.cursor, sources));
    } catch (err: any) {
      setCtxErr(err?.response?.data?.message || 'Could not load the surrounding lines');
    }
  };

  return (
    <LogRow level={e.level} open={open}>
      <LogHead type="button" aria-expanded={open} onClick={() => setOpen(o => !o)} title={`${e.ts} (UTC)`}>
        <span className="time">{fmtTs(e)}</span>
        <span className="lvl">
          <Tag tone={levelTone(e.level)}>{e.level_name}</Tag>
        </span>
        <span className="src">{e.source}</span>
        <span className="msg">
          {e.tag && !isReq ? <strong>[{e.tag}] </strong> : null}
          {isReq ? <RequestSummary e={e} /> : e.tag ? e.message.replace(/^\[[^\]]+\]\s?/, '') : e.message}
          {e.detail ? <span style={{ color: palette.slate }}> (+{e.detail.length} lines)</span> : null}
        </span>
      </LogHead>
      {open ? (
        <LogBody>
          <Mono>
            {whenExact(e.ts)} your time · {e.ts} UTC · {e.unit || e.ident || 'system'}
            {e.pid ? ` · pid ${e.pid}` : ''}
          </Mono>
          {/* The raw line only when it is not already shown, parsed, below. */}
          {!e.fields || e.detail ? (
            <pre>{e.message}{e.detail ? `\n${e.detail.join('\n')}` : ''}</pre>
          ) : null}
          {e.fields ? <pre>{JSON.stringify(e.fields, null, 2)}</pre> : null}
          <Toolbar>
            {e.fields?.id ? (
              <Btn onClick={() => onFilter('request_id', String(e.fields?.id))}>Everything for this request</Btn>
            ) : null}
            {e.fields?.org ? <Btn onClick={() => onFilter('org', String(e.fields?.org))}>This organization</Btn> : null}
            {e.fields?.uid ? <Btn onClick={() => onFilter('uid', String(e.fields?.uid))}>This user</Btn> : null}
            {canContext ? <Btn onClick={loadContext}>Lines around this</Btn> : null}
            <Btn
              onClick={() =>
                navigator.clipboard?.writeText(
                  JSON.stringify({ ts: e.ts, source: e.source, level: e.level_name, message: e.message, detail: e.detail, fields: e.fields }, null, 2)
                )
              }
            >
              Copy
            </Btn>
          </Toolbar>
          {ctxErr ? <ErrorBox>{ctxErr}</ErrorBox> : null}
          {ctx ? (
            <pre aria-label="Surrounding lines">
              {[...ctx.before].reverse().map(x => `  ${x.ts}  ${x.source}  ${x.message}`).join('\n')}
              {`\n▶ ${ctx.at?.ts}  ${ctx.at?.source}  ${ctx.at?.message}\n`}
              {ctx.after.map(x => `  ${x.ts}  ${x.source}  ${x.message}`).join('\n')}
            </pre>
          ) : null}
        </LogBody>
      ) : null}
    </LogRow>
  );
};

const LIVE_EVERY_MS = 5000;
const KEEP_MAX = 3000;

export const SystemLogsScreen: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const sources = (params.get('sources') || DEFAULT_SOURCES.join(',')).split(',').filter(Boolean);
  const level = params.get('level') || '7';
  const range = params.get('range') || '1h';
  const status = params.get('status') || '';
  const method = params.get('method') || '';
  const tag = params.get('tag') || '';
  const regex = params.get('regex') === '1';

  // Text boxes are local until applied, so a keystroke is not a journal scan.
  const [text, setText] = useState<Record<string, string>>(() =>
    Object.fromEntries(TEXT_FILTERS.map(k => [k, params.get(k) || '']))
  );
  useEffect(() => {
    setText(Object.fromEntries(TEXT_FILTERS.map(k => [k, params.get(k) || ''])));
  }, [params]);

  const [page, setPage] = useState<api.LogPage | null>(null);
  const [items, setItems] = useState<api.LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const top = useRef<string | null>(null);
  const seq = useRef(0);

  const set = useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(params);
      Object.entries(patch).forEach(([k, val]) => {
        if (val == null || val === '') next.delete(k);
        else next.set(k, val);
      });
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  const query = useMemo(() => {
    const r = RANGES.find(x => x.value === range);
    const q: api.ListParams = {
      sources: sources.join(','),
      level,
      status,
      method,
      tag,
      regex: regex ? '1' : undefined,
      limit: 200,
    };
    TEXT_FILTERS.forEach(k => {
      const val = params.get(k);
      if (val) q[k] = val;
    });

    return { q, sinceMs: r && r.ms ? r.ms : null };
  }, [params]);

  const run = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const since = query.sinceMs ? new Date(Date.now() - query.sinceMs).toISOString() : undefined;
      const p = await api.getLogs({ ...query.q, since });
      if (mine !== seq.current) return;
      setPage(p);
      setItems(p.items);
      top.current = p.top_cursor;
    } catch (e: any) {
      if (mine !== seq.current) return;
      setError(e?.response?.data?.message || e?.message || 'Could not load logs');
      setItems([]);
      setPage(null);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    run();
  }, [run]);

  const loadOlder = async () => {
    if (!page?.next_cursor) return;
    setMore(true);
    try {
      const since = query.sinceMs ? new Date(Date.now() - query.sinceMs).toISOString() : undefined;
      const p = await api.getLogs({ ...query.q, since, cursor: page.next_cursor });
      setPage(p);
      setItems(prev => [...prev, ...p.items].slice(0, KEEP_MAX));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not load older lines');
    } finally {
      setMore(false);
    }
  };

  // LIVE TAIL. Asks for what arrived after the newest line already shown, in
  // time order, and prepends it. The cursor advances to the last line SCANNED,
  // not the last one matched, so a quiet filter never re-reads the same stretch.
  useEffect(() => {
    if (!live) return undefined;
    const t = setInterval(async () => {
      if (!top.current) return;
      try {
        const p = await api.getLogs({ ...query.q, direction: 'newer', cursor: top.current, limit: 500 });
        if (p.next_cursor) top.current = p.next_cursor;
        if (p.items.length) setItems(prev => [...[...p.items].reverse(), ...prev].slice(0, KEEP_MAX));
      } catch {
        /* the next tick retries; a transient error must not stop the tail */
      }
    }, LIVE_EVERY_MS);

    return () => clearInterval(t);
  }, [live, query]);

  const apply = () => set(Object.fromEntries(TEXT_FILTERS.map(k => [k, text[k].trim()])));
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') apply();
  };
  const toggleSource = (key: string) => {
    let next = sources.includes(key) ? sources.filter(s => s !== key) : [...sources, key];
    if (key === 'all' && next.includes('all')) next = ['all'];
    else next = next.filter(s => s !== 'all');
    set({ sources: (next.length ? next : DEFAULT_SOURCES).join(',') });
  };
  const allSources = page?.sources || [
    { key: 'app', label: 'App (Node)' },
    { key: 'proxy', label: 'Proxy (Caddy)' },
    { key: 'backup', label: 'Database backup' },
    { key: 'ssh', label: 'SSH logins' },
    { key: 'updates', label: 'OS updates' },
    { key: 'kernel', label: 'Kernel' },
    { key: 'all', label: 'Everything on the box' },
  ];
  const filtersOn = TEXT_FILTERS.some(k => params.get(k)) || status || method || tag || level !== '7';

  return (
    <>
      <Card>
        <CardBody>
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
            <Chips role="group" aria-label="Sources">
              {allSources.map(s => (
                <label key={s.key}>
                  <input type="checkbox" checked={sources.includes(s.key)} onChange={() => toggleSource(s.key)} />
                  <span>{s.label}</span>
                </label>
              ))}
            </Chips>
            <FilterGrid>
              <Select aria-label="Time range" value={range} onChange={e => set({ range: e.target.value })}>
                {RANGES.map(r => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </Select>
              <Select aria-label="Level" value={level} onChange={e => set({ level: e.target.value === '7' ? null : e.target.value })}>
                {LEVELS.map(l => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </Select>
              <Select aria-label="Tag" value={tag} onChange={e => set({ tag: e.target.value })}>
                <option value="">Any tag</option>
                {TAGS.map(t => (
                  <option key={t} value={t}>
                    [{t}]
                  </option>
                ))}
              </Select>
              <Select aria-label="HTTP status" value={status} onChange={e => set({ status: e.target.value })}>
                <option value="">Any status</option>
                {['2xx', '3xx', '4xx', '5xx'].map(s => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
              <Select aria-label="HTTP method" value={method} onChange={e => set({ method: e.target.value })}>
                <option value="">Any method</option>
                {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(m => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Select>
              <SearchBox
                aria-label="Minimum duration in milliseconds"
                placeholder="Slower than (ms)"
                inputMode="numeric"
                value={text.min_ms}
                onChange={e => setText(t => ({ ...t, min_ms: e.target.value }))}
                onKeyDown={onKey}
              />
              <SearchBox
                aria-label="Route or path contains"
                placeholder="Route or path contains"
                value={text.route}
                onChange={e => setText(t => ({ ...t, route: e.target.value }))}
                onKeyDown={onKey}
              />
              <SearchBox
                aria-label="Organization id"
                placeholder="Organization id"
                value={text.org}
                onChange={e => setText(t => ({ ...t, org: e.target.value }))}
                onKeyDown={onKey}
              />
              <SearchBox
                aria-label="User id"
                placeholder="User id"
                value={text.uid}
                onChange={e => setText(t => ({ ...t, uid: e.target.value }))}
                onKeyDown={onKey}
              />
              <SearchBox
                aria-label="Request id"
                placeholder="Request id"
                value={text.request_id}
                onChange={e => setText(t => ({ ...t, request_id: e.target.value }))}
                onKeyDown={onKey}
              />
            </FilterGrid>
            <Toolbar>
              <SearchBox
                aria-label="Search the log text"
                placeholder={regex ? 'Regular expression (case-insensitive)' : 'Search the log text'}
                value={text.q}
                onChange={e => setText(t => ({ ...t, q: e.target.value }))}
                onKeyDown={onKey}
                style={{ maxWidth: 520 }}
              />
              <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: typography.size.xs }}>
                <input type="checkbox" checked={regex} onChange={e => set({ regex: e.target.checked ? '1' : null })} />
                regex
              </label>
              <Btn variant="primary" onClick={apply}>
                Apply
              </Btn>
              {filtersOn ? (
                <Btn
                  onClick={() =>
                    set({ level: null, status: null, method: null, tag: null, regex: null, ...Object.fromEntries(TEXT_FILTERS.map(k => [k, null])) })
                  }
                >
                  Clear filters
                </Btn>
              ) : null}
              <Btn onClick={run} disabled={loading}>
                Refresh
              </Btn>
              <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: typography.size.xs }}>
                <input type="checkbox" checked={live} onChange={e => setLive(e.target.checked)} />
                Live tail (every {LIVE_EVERY_MS / 1000}s)
              </label>
            </Toolbar>
          </div>
        </CardBody>
      </Card>

      {page && page.backend === 'buffer' ? (
        <NoteBox>
          Showing this app process&apos;s own recent output only — not the box&apos;s journal. Reason:{' '}
          {page.reason}.
        </NoteBox>
      ) : null}
      {page?.hint ? <NoteBox>{page.hint}</NoteBox> : null}
      {error ? <ErrorBox role="alert">{error}</ErrorBox> : null}

      <Card>
        <CardHead>
          <CardTitle>
            {loading ? 'Reading…' : `${items.length.toLocaleString()} ${items.length === 1 ? 'line' : 'lines'}`}
          </CardTitle>
          {page ? (
            <Mono>
              {page.backend} · scanned {page.scanned.toLocaleString()}
              {page.stopped_by === 'time' ? ' · stopped at the time limit' : ''}
              {page.stopped_by === 'scan_cap' ? ' · stopped at the scan limit' : ''}
              {page.exhausted ? ' · reached the start of the range' : ''}
            </Mono>
          ) : null}
        </CardHead>
        {loading && !items.length ? (
          <CardBody>
            <Loading />
          </CardBody>
        ) : !items.length ? (
          <Empty>
            {page?.exhausted
              ? 'Nothing matched in this range.'
              : 'Nothing matched in the stretch scanned so far — load older to keep searching.'}
          </Empty>
        ) : (
          <LogList aria-label="Log lines, newest first">
            {items.map(e => (
              <LogLine
                key={e.cursor}
                e={e}
                sources={sources.join(',')}
                canContext={page?.backend === 'journal'}
                onFilter={(k, val) => set({ [k]: val, range: 'all' })}
              />
            ))}
          </LogList>
        )}
      </Card>

      {page && !page.exhausted && page.next_cursor ? (
        <Toolbar>
          <Btn onClick={loadOlder} disabled={more}>
            {more ? 'Reading…' : 'Load older'}
          </Btn>
        </Toolbar>
      ) : null}
    </>
  );
};
